/*>
* @license
* Copyright (c) 2014-2022 ABILITY Network, Inc., an INOVALON, Inc. company. All Rights Reserved.
* 
* This computer program is CONFIDENTIAL and a TRADE SECRET of ABILITY Network, Inc.
* The receipt or possession of this program does not convey any rights to use,
* reproduce or disclose its contents in whole or in part, without the specific
* written consent of ABILITY Network, Inc. Any use, reproduction or disclosure of
* this program without the express written consent of ABILITY Network, Inc. is a
* violation of the copyright laws and may subject you to criminal prosecution.
<*/
import { injectable } from 'inversify'
import { action, observable } from 'mobx'
import JwtDecode from 'jwt-decode'
import { injectProperty } from '../../configuration/ServiceLocator'
import { IContentApiClient } from '../ContentApiClient/contracts'
import { UserVote, IArticle, IArticleRating, IContentStateManager } from './contracts'
import { SettingsManager } from '../../configuration/SettingsManager'

@injectable()
export class ContentStateManager implements IContentStateManager {
	@observable
	article: IArticle

	@observable
	rating: IArticleRating

	@injectProperty('IContentApiClient')
	public ContentApiClient!: IContentApiClient

	constructor () {
		this.article = { contentItemId: '', path: '', displayText: '', publishedUtc: new Date(), markup: '', productCode: '', productName: '', sectionId: '', sectionName: '' } as IArticle
		this.rating = { contentItemId: '', countLikes: 0, countDislikes: 0, vote: UserVote.none } as IArticleRating
	}

	@action setArticle(article: IArticle): void {
		this.article = article
	}

	@action setArticleRating(rating: IArticleRating): void {
		this.rating = rating
	}

	@action getArticle(path: string): Promise<IArticle> {
		return new Promise<IArticle>((resolve, reject) => {
			this.ContentApiClient.getArticle(path)
				.then(response => {
					this.getProductName(response.productCode).then(name => (response.productName = name.split(',')[0], response.productCode = name.split(',')[1]))
					this.setArticle(response)
					resolve(response)
				})
				.catch(error => {
					console.log(error)
					reject(error)
				})
			// .catch(error => {
			// 	// Display user friendly message
			// 	reject(error)

			// })
		})
	}

	@action voteOnArticle(contentItemId: string, userId: string, newVote: UserVote, currentVote: UserVote): Promise<IArticleRating> {
		if (currentVote === UserVote.none && newVote !== UserVote.none) {
			return new Promise<IArticleRating>((resolve, reject) => {
				this.ContentApiClient.vote(contentItemId, userId, newVote)
					.then(response => {
						this.setArticleRating(response)
						resolve(response)
					})
					.catch(error => {
						// Display user friendly message
						reject(error)
					})
			})
		} else if (currentVote !== UserVote.none && newVote === UserVote.none) {
			return new Promise<IArticleRating>((resolve, reject) => {
				this.ContentApiClient.deleteVote(contentItemId, userId, currentVote)
					.then(response => {
						this.setArticleRating(response)
						resolve(response)
					})
					.catch(error => {
						console.log(error)
						throw new Error('An error occurred while deleting the User Vote.')
					})
				// .catch(error => {
				// 	// Display user friendly message
				// 	reject(error)
				// })
			})
		} else if (newVote !== currentVote) {
			return new Promise<IArticleRating>((resolve, reject) => {
				this.ContentApiClient.changeVote(contentItemId, userId, newVote)
					.then(response => {
						this.setArticleRating(response)
						resolve(response)
					})
					.catch(error => {
						console.log(error)
						throw new Error('An error occurred while changing the User Vote.')
					})
				// .catch(error => {
				// 	// Display user friendly message
				// 	reject(error)
				// })
			})
		} else {
			throw 'User has not changed vote to the article.'
		}
	}

	@action getArticleRating(contentItemId: string, userId: string): Promise<IArticleRating> {
		return new Promise<IArticleRating>((resolve, reject) => {
			this.ContentApiClient.getRating(contentItemId, userId)
				.then(response => {
					this.setArticleRating(response)
					resolve(response)
				})
				.catch(error => {
					console.log(error)
					reject(error)
				})
			// .catch(error => {
			// 	// Display user friendly message
			// 	reject(error)
			// })
		})
	}

	getUserId(): Promise<string> {
		return new Promise<string>((resolve, reject) => {
			const token = localStorage.getItem(SettingsManager.REACT_APP_ProductCategories_TOKEN_NAME)
			const tokenObject: any = JwtDecode(JSON.stringify(token))
			const userId: string = tokenObject.userId as string
			if (userId === '') {
				reject('User ID not found in JWT token.')
			} else {
				resolve(userId)
			}
		})
	}

	getProductName(productCode: string): Promise<string> {
		return new Promise<string>(resolve => {
			let productName = ''
			const productToken = localStorage.getItem(SettingsManager.REACT_APP_ProductCategories_TOKEN_NAME)
			const products: any = JwtDecode(JSON.stringify(productToken))
			for (let i = 0; i < products.AbilityProductCategoryClaimV2.productCategories.length; i++) {
				const item: any = products.AbilityProductCategoryClaimV2.productCategories[i]
				for (let j = 0; j < item.HomeIndexProductsViewModel.length; j++) {
					const productItem: any = item.HomeIndexProductsViewModel[j]
					if (productCode.toLowerCase() === productItem.Code.toLowerCase()) {
						productName = productItem.Name + ',' + productItem.Code
						break
					}
				}
				if (productName !== '') break
			}
			resolve(productName)
		})
	}
}
